import * as React from 'react'
import { Accordion, Box, Loader } from 'cs-kevlar-react'
import {
  createStyles,
  makeStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { AuditEventSearchResult, IAuditEventData } from '../../api'
import { useAuditApi } from 'hooks'
import { EventOverview } from './EventOverview'
import { UnavailableDetails } from './details/UnavailableDetails'
import { CustomerDeviceDetails } from './details/CustomerDeviceDetails'
import { CustomerAdminDetails } from './details/CustomerAdminDetails'
import { CustomerUserDetails } from './details/CustomerUserDetails'
import { ApiTokenDetails } from './details/ApiTokenDetails'
import { SupportUserDetails } from './details/SupportUserDetails'
import { ReportAccessDetails } from './details/ReportAccessDetails'
import { CustomerAgencyDetails } from './details/CustomerAgencyDetails'
import { CustomerAgencyDataSearchDetails } from './details/CustomerAgencyDataSearchDetails'
import { CustomerAgencyDocumentQueryDetails } from './details/CustomerAgencyDocumentQueryDetails'
import { CustomerAgencyDatabaseShardMigrationDetails } from './details/CustomerAgencyDatabaseShardMigrationDetails'
import { CustomerLicensedAgencyDetails } from './details/CustomerLicensedAgencyDetails'
import { CustomerLicensedAgencyConfigurationDetails } from './details/CustomerLicensedAgencyConfigurationDetails'
import { CustomerInterfaceDetails } from './details/CustomerInterfaceDetails'
import { DiagnosticDetails } from './details/DiagnosticDetails'
import { UserSignInDetails } from './details/UserSignInDetails'
import { PageViewDetails } from './details/PageViewDetails'
import {CustomerCustomMapLayerDetails} from "components/audit/details/CustomerCustomMapLayerDetails";
import { CustomerDeviceMediaRecoveryDetails } from './details/CustomerDeviceMediaRecoveryDetails'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    eventDetailsContainer: {
      '& .MuiAccordionDetails-root': {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(0),
      },
    },
    loaderContainer: {
      display: 'flex',
      flex: 1,
      justifyContent: 'center',
      minWidth: 0,
      padding: theme.spacing(8),
    },
  }),
)

const renderDetails = (
  event: AuditEventSearchResult,
  eventData: IAuditEventData | null,
) => {
  if (!event.eventType || !eventData) return <UnavailableDetails />

  if (event.eventType.startsWith('User:SignIn'))
    return <UserSignInDetails eventData={eventData} />

  if (event.eventType.startsWith('User:PageView'))
    return <PageViewDetails eventData={eventData} />

  if (event.eventType.startsWith('ApiToken:'))
    return <ApiTokenDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerAdminUser:'))
    return <CustomerAdminDetails eventData={eventData} />

  if (
    event.eventType.startsWith(
      'CustomerAgency:Configuration:Database:Migrated',
    )
  )
    return <CustomerAgencyDatabaseShardMigrationDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerAgency:Data:Searched'))
    return <CustomerAgencyDataSearchDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerAgency:Documents:Queried'))
    return <CustomerAgencyDocumentQueryDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerAgency:'))
    return <CustomerAgencyDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerLicensedAgency:'))
    return <CustomerLicensedAgencyDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerLicensedAgencyConfiguration:'))
    return <CustomerLicensedAgencyConfigurationDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerDevice:MediaRecovery:Recovered'))
    return <CustomerDeviceMediaRecoveryDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerDevice:'))
    return <CustomerDeviceDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerLicensedUser:'))
    return <CustomerUserDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerInterface:'))
    return <CustomerInterfaceDetails eventData={eventData} />

  if (event.eventType.startsWith('Report:'))
    return <ReportAccessDetails eventData={eventData} />

  if (event.eventType.startsWith('SupportUser:'))
    return <SupportUserDetails eventData={eventData} />

  if (event.eventType.startsWith('CustomerCustomMapLayer:'))
    return <CustomerCustomMapLayerDetails eventData={eventData} />

  if (
    event.eventType.startsWith('Diagnostic:') ||
    event.eventType.startsWith('System:')
  )
    return <DiagnosticDetails eventData={eventData} />

  return <UnavailableDetails />
}

interface EventDetailsProps {
  event: AuditEventSearchResult
}

export const EventDetails: React.FunctionComponent<EventDetailsProps> = ({
  event,
}) => {
  const classes = useStyles({})
  const api = useAuditApi()

  const [isLoadingDetails, setIsLoadingDetails] = React.useState(false)
  const [details, setDetails] = React.useState<IAuditEventData | null>(null)

  React.useEffect(() => {
    setDetails(null)
    setIsLoadingDetails(true)

    api
      .getAuditEventData(event.eventId)
      .then((x) => {
        if (x.status !== 200 || !x.data) {
          setIsLoadingDetails(false)
          return
        }

        setDetails(x.data)
        setIsLoadingDetails(false)
      })
      .catch((x) => {
        setIsLoadingDetails(false)
      })
  }, [event.eventId])

  return (
    <React.Fragment>
      <Accordion id="AuditSummary" title="Audit Details" initiallyExpanded>
        <EventOverview event={event} />
      </Accordion>

      <Accordion
        id="EventDetails"
        title="Details"
        className={classes.eventDetailsContainer}
        initiallyExpanded
      >
        {isLoadingDetails && (
          <Box className={classes.loaderContainer}>
            <Loader size="sm" text="Loading" disableVerticalGutters />
          </Box>
        )}

        {!isLoadingDetails && renderDetails(event, details)}
      </Accordion>
    </React.Fragment>
  )
}
